import { sampleStoreRegion } from './tile-sampling';
import { clipTileCoverage } from './tile-coverage';
import { nativeDocumentTextGeneration } from '../public-format/native-text';
import { nativeDocumentImage, nativeDocumentImageGeneration } from '../public-format/native-images';
import { drawImageInFrame } from '../public-format/image-fit';
import { IDENTITY_MATRIX, invertMatrix, multiplyMatrix, transformPoint, transformRectBoundsPrecise, type Matrix, type Rect } from '../core/geometry';
import type { AdjustmentLayer, BlendMode, EditorDocument, GroupLayer, Layer, LayerMask, RasterLayer } from '../core/model';
import { RasterTileStore, type TileEntry } from '../core/tiles';
import { renderExtensionLayer } from './extension-renderers';
import { extensionRendererGeneration } from './extension-renderers';
import { applyTonePixels } from '../filters/tone';
import type { DamageSet } from '../core/damage';
import { findLayer, findLayerParent, layerBounds, layerDocumentTransform, walkLayers } from '../core/model';
import { layerStyle } from '../core/layer-styles';
import { applySmartFiltersToPixels, smartFilters } from '../core/smart-filters';

export interface Viewport {
  width: number;
  height: number;
  zoom: number;
  panX: number;
  panY: number;
  dpr: number;
  transparentBackground?: boolean;
}

export interface RenderStats {
  renderedTiles: number;
  elapsedMs: number;
  backend: 'canvas2d';
  tileCacheHits: number;
  layerCacheHits: number;
  cacheHitRate: number;
  canvasAllocations: number;
  inputToPresentMs?: number;
}

export interface RendererCacheStats {
  tileHits: number;
  tileMisses: number;
  layerHits: number;
  layerMisses: number;
  canvasAllocations: number;
  retainedSurfaces: number;
  retainedBytes: number;
}

export interface CanvasLimits { maxDimension: number; maxArea: number }
export interface FlattenedTile { canvas: HTMLCanvasElement; x: number; y: number; scale: number }

let detectedCanvasLimits: CanvasLimits | undefined;
export function probeCanvasLimits(): CanvasLimits {
  if (detectedCanvasLimits) return detectedCanvasLimits;
  const probe = globalThis.document.createElement('canvas');
  let maxDimension = 8_192;
  for (const candidate of [16_384, 32_767, 65_535]) {
    probe.width = candidate;
    probe.height = 1;
    if (probe.width !== candidate || !probe.getContext('2d')) break;
    maxDimension = candidate;
  }
  detectedCanvasLimits = { maxDimension, maxArea: 67_108_864 };
  return detectedCanvasLimits;
}

function assertCanvasAllocation(width: number, height: number): void {
  const limits = probeCanvasLimits();
  const pixelWidth = Math.max(1, Math.ceil(width));
  const pixelHeight = Math.max(1, Math.ceil(height));
  if (!Number.isFinite(width) || !Number.isFinite(height) || pixelWidth > limits.maxDimension || pixelHeight > limits.maxDimension || pixelWidth * pixelHeight > limits.maxArea) {
    throw new Error(`Requested ${pixelWidth.toLocaleString()} × ${pixelHeight.toLocaleString()} canvas exceeds this browser's safe canvas limit.`);
  }
}

/**
 * A bounded preview render (ADR-009 §5.1 rule 5). The caller (the agent
 * gateway's preview policy) decides `scale`; the renderer only draws.
 */
export interface PreviewRenderOptions {
  /** Output pixels per document pixel. */
  scale: number;
  /** Document-space crop; default the whole document. It is intersected with the document. */
  region?: Rect;
  /** Draw only these layers: their subtrees, inside their ancestors' compositing. */
  isolate?: readonly string[];
  /** Outline these layers' document bounds on top. */
  highlight?: readonly string[];
  /** Solid backdrop ('#rrggbb'); transparent when omitted. */
  background?: string;
}

export interface PreviewRender { canvas: HTMLCanvasElement; region: Rect; bounds: Record<string, Rect | null> }

export interface Renderer {
  render(document: EditorDocument, target: HTMLCanvasElement, viewport: Viewport): RenderStats;
  schedule(document: EditorDocument, target: HTMLCanvasElement, viewport: Viewport, onPresent?: (stats: RenderStats) => void): void;
  invalidate(document: EditorDocument, damage: DamageSet): void;
  flatten(document: EditorDocument, scale?: number): HTMLCanvasElement;
  flattenTiles(document: EditorDocument, scale?: number, tileSize?: number): FlattenedTile[];
  renderPreview?(document: EditorDocument, options: PreviewRenderOptions): PreviewRender;
  disposeDocument(document: EditorDocument): void;
  cacheStats(): RendererCacheStats;
  dispose(): void;
}

interface SurfaceEntry { key: string; canvas: HTMLCanvasElement }
interface DocumentRenderCache { layers: Map<string, SurfaceEntry> }

class CanvasPool {
  private canvases: HTMLCanvasElement[] = [];
  allocations = 0;

  acquire(width: number, height: number): HTMLCanvasElement {
    assertCanvasAllocation(width, height);
    const canvas = this.canvases.pop() ?? (() => { this.allocations += 1; return globalThis.document.createElement('canvas'); })();
    canvas.width = Math.max(1, Math.floor(width));
    canvas.height = Math.max(1, Math.floor(height));
    return canvas;
  }

  release(canvas: HTMLCanvasElement): void {
    if (this.canvases.length >= 24) return;
    const context = canvas.getContext('2d');
    context?.clearRect(0, 0, canvas.width, canvas.height);
    this.canvases.push(canvas);
  }

  clear(): void { this.canvases = []; }
}

interface RenderRuntime {
  document: EditorDocument;
  documentCache: DocumentRenderCache;
  tileSurfaces: WeakMap<RasterTileStore, Map<string, { revision: number; canvas: HTMLCanvasElement }>>;
  pool: CanvasPool;
  stats: RendererCacheStats;
}

const canvasBlendModes: Record<BlendMode, GlobalCompositeOperation> = {
  normal: 'source-over',
  multiply: 'multiply',
  screen: 'screen',
  overlay: 'overlay',
  darken: 'darken',
  lighten: 'lighten',
  'color-dodge': 'color-dodge',
  'color-burn': 'color-burn',
  'hard-light': 'hard-light',
  'soft-light': 'soft-light',
  difference: 'difference',
  exclusion: 'exclusion',
};

let retainedCanvasAllocations = 0;

function createCanvas(width: number, height: number): HTMLCanvasElement {
  assertCanvasAllocation(width, height);
  retainedCanvasAllocations += 1;
  const canvas = globalThis.document.createElement('canvas');
  canvas.width = Math.max(1, Math.floor(width));
  canvas.height = Math.max(1, Math.floor(height));
  return canvas;
}

function requireContext(canvas: HTMLCanvasElement, options?: CanvasRenderingContext2DSettings): CanvasRenderingContext2D {
  const context = canvas.getContext('2d', options);
  if (!context) throw new Error('Canvas 2D is unavailable in this browser.');
  return context;
}

function viewportMatrix(viewport: Viewport, layerMatrix: Matrix): Matrix {
  return [
    viewport.dpr * viewport.zoom * layerMatrix[0],
    viewport.dpr * viewport.zoom * layerMatrix[1],
    viewport.dpr * viewport.zoom * layerMatrix[2],
    viewport.dpr * viewport.zoom * layerMatrix[3],
    viewport.dpr * (viewport.panX + viewport.zoom * layerMatrix[4]),
    viewport.dpr * (viewport.panY + viewport.zoom * layerMatrix[5]),
  ];
}

function setMatrix(context: CanvasRenderingContext2D, matrix: Matrix): void {
  context.setTransform(matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5]);
}

function visibleLocalBounds(viewport: Viewport, layerMatrix: Matrix): Rect {
  const inverse = invertMatrix(layerMatrix);
  const documentBounds = {
    x: -viewport.panX / viewport.zoom,
    y: -viewport.panY / viewport.zoom,
    width: viewport.width / viewport.zoom,
    height: viewport.height / viewport.zoom,
  };
  if (!inverse) return documentBounds;
  const corners = [
    transformPoint(inverse, { x: documentBounds.x, y: documentBounds.y }),
    transformPoint(inverse, { x: documentBounds.x + documentBounds.width, y: documentBounds.y }),
    transformPoint(inverse, { x: documentBounds.x, y: documentBounds.y + documentBounds.height }),
    transformPoint(inverse, { x: documentBounds.x + documentBounds.width, y: documentBounds.y + documentBounds.height }),
  ];
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

function tileCanvas(store: RasterTileStore, tile: TileEntry, runtime: RenderRuntime): HTMLCanvasElement {
  let surfaces = runtime.tileSurfaces.get(store);
  if (!surfaces) {
    surfaces = new Map();
    runtime.tileSurfaces.set(store, surfaces);
  }
  const key = RasterTileStore.key(tile.x, tile.y);
  const revision = store.tileRevision(tile.x, tile.y);
  const cached = surfaces.get(key);
  if (cached?.revision === revision) {
    runtime.stats.tileHits += 1;
    return cached.canvas;
  }
  runtime.stats.tileMisses += 1;
  const dimensions = store.tileDimensions(tile.x, tile.y);
  const canvas = runtime.pool.acquire(dimensions.width, dimensions.height);
  requireContext(canvas).putImageData(new ImageData(new Uint8ClampedArray(tile.pixels), dimensions.width, dimensions.height), 0, 0);
  surfaces.set(key, { revision, canvas });
  if (cached) {
    runtime.stats.retainedBytes -= cached.canvas.width * cached.canvas.height * 4;
    runtime.pool.release(cached.canvas);
  } else runtime.stats.retainedSurfaces += 1;
  runtime.stats.retainedBytes += canvas.width * canvas.height * 4;
  return canvas;
}

function drawStore(
  context: CanvasRenderingContext2D,
  store: RasterTileStore,
  layerMatrix: Matrix,
  viewport: Viewport,
  runtime: RenderRuntime,
  defaultAlpha: 0 | 255 = 0,
  smoothing = viewport.zoom < 8,
): number {
  const matrix = viewportMatrix(viewport, layerMatrix);
  const inverse = invertMatrix(matrix);
  if (!inverse) return 0;
  const bounds = visibleLocalBounds(viewport, layerMatrix);
  // Assemble source blocks independently of storage tiles. Small sources use
  // one surface, preserving the browser's single-image sampling phase.
  const span = 1024;
  const minX = Math.max(0, Math.floor((bounds.x - 1) / span));
  const minY = Math.max(0, Math.floor((bounds.y - 1) / span));
  const maxX = Math.min(Math.ceil(store.width / span) - 1, Math.floor((bounds.x + bounds.width + 1) / span));
  const maxY = Math.min(Math.ceil(store.height / span) - 1, Math.floor((bounds.y + bounds.height + 1) / span));
  const gutter = Math.ceil(Math.max(Math.abs(inverse[0]) + Math.abs(inverse[2]), Math.abs(inverse[1]) + Math.abs(inverse[3]))) + 1;
  if (Math.min(store.width, span + 2 * gutter) > 2048 || Math.min(store.height, span + 2 * gutter) > 2048) {
    const sampled = sampleStoreRegion(store, matrix, inverse, context.canvas.width, context.canvas.height, defaultAlpha, smoothing);
    if (!sampled) return 0;
    const patch = runtime.pool.acquire(sampled.image.width, sampled.image.height);
    requireContext(patch).putImageData(sampled.image, 0, 0);
    context.save(); context.setTransform(1, 0, 0, 1, 0, 0); context.beginPath();
    sampled.corners.forEach((point, index) => { if (index === 0) context.moveTo(point.x, point.y); else context.lineTo(point.x, point.y); });
    context.closePath(); context.clip(); context.drawImage(patch, sampled.x, sampled.y); context.restore();
    runtime.pool.release(patch);
    return store.visibleEntries(bounds).length;
  }
  for (let y = minY; y <= maxY; y++) for (let x = minX; x <= maxX; x++) {
    const tile = { x: x * span, y: y * span, width: Math.min(span, store.width - x * span), height: Math.min(span, store.height - y * span) };
    // Neighbor pixels let the browser interpolate across the tile boundary.
    // Scratch dimensions are capped above; huge compressed sources use the
    // destination-sized sampler instead of allocating a full-source canvas.
    const sourceX = Math.max(0, tile.x - gutter); const sourceY = Math.max(0, tile.y - gutter);
    const width = Math.min(store.width, tile.x + tile.width + gutter) - sourceX;
    const height = Math.min(store.height, tile.y + tile.height + gutter) - sourceY;
    const neighbors = store.visibleEntries({ x: sourceX, y: sourceY, width, height });
    if (!neighbors.length && defaultAlpha === 0) continue;
    const patch = runtime.pool.acquire(width, height);
    const patchContext = requireContext(patch);
    if (defaultAlpha === 255) { patchContext.fillStyle = '#fff'; patchContext.fillRect(0, 0, width, height); }
    for (const entry of neighbors) {
      const dx = entry.x * store.tileSize - sourceX; const dy = entry.y * store.tileSize - sourceY;
      const image = tileCanvas(store, entry, runtime);
      patchContext.clearRect(dx, dy, image.width, image.height);
      patchContext.drawImage(image, dx, dy);
    }
    context.save();
    clipTileCoverage(context, inverse, matrix, tile, store.width, store.height, context.canvas.width, context.canvas.height);
    setMatrix(context, matrix);
    context.imageSmoothingEnabled = smoothing;
    context.drawImage(patch, sourceX, sourceY);
    context.restore();
    runtime.pool.release(patch);
  }
  return store.visibleEntries(bounds).length;
}

function maskCanvas(mask: LayerMask, layerMatrix: Matrix, viewport: Viewport, runtime: RenderRuntime): HTMLCanvasElement {
  const canvas = runtime.pool.acquire(viewport.width * viewport.dpr, viewport.height * viewport.dpr);
  drawStore(requireContext(canvas), mask.tiles, layerMatrix, viewport, runtime, mask.defaultAlpha, true);
  return canvas;
}

function renderRaster(layer: RasterLayer, viewport: Viewport, layerMatrix: Matrix, runtime: RenderRuntime): { canvas: HTMLCanvasElement; tiles: number } {
  const canvas = runtime.pool.acquire(viewport.width * viewport.dpr, viewport.height * viewport.dpr);
  const context = requireContext(canvas);
  const tiles = drawStore(context, layer.raster, layerMatrix, viewport, runtime);
  return { canvas, tiles };
}

function renderExtension(layer: Layer, viewport: Viewport, parentTransform: Matrix, runtime: RenderRuntime): { canvas: HTMLCanvasElement; tiles: number } {
  const canvas = runtime.pool.acquire(viewport.width * viewport.dpr, viewport.height * viewport.dpr);
  const context = requireContext(canvas);
  renderExtensionLayer(layer, { context, viewport, parentTransform, document: runtime.document });
  return { canvas, tiles: 0 };
}

function applyAdjustment(canvas: HTMLCanvasElement, layer: AdjustmentLayer, viewport: Viewport, parentTransform: Matrix, runtime: RenderRuntime, clipMask?: HTMLCanvasElement): void {
  const adjusted = runtime.pool.acquire(canvas.width, canvas.height);
  const adjustedContext = requireContext(adjusted, { willReadFrequently: true });
  adjustedContext.drawImage(canvas, 0, 0);
  const image = adjustedContext.getImageData(0, 0, adjusted.width, adjusted.height);
  applyTonePixels(image.data, layer.adjustment, 1);
  adjustedContext.putImageData(image, 0, 0);
  if (layer.mask?.enabled) {
    adjustedContext.globalCompositeOperation = 'destination-in';
    const surface = maskCanvas(layer.mask, multiplyMatrix(parentTransform, layer.transform), viewport, runtime);
    adjustedContext.drawImage(surface, 0, 0);
    runtime.pool.release(surface);
  }
  if (clipMask) {
    adjustedContext.globalCompositeOperation = 'destination-in';
    adjustedContext.drawImage(clipMask, 0, 0);
  }
  const context = requireContext(canvas);
  context.globalAlpha = layer.opacity * layer.fillOpacity;
  context.globalCompositeOperation = canvasBlendModes[layer.blendMode];
  context.drawImage(adjusted, 0, 0);
  context.globalAlpha = 1;
  context.globalCompositeOperation = 'source-over';
  runtime.pool.release(adjusted);
}

function styledSurface(source: HTMLCanvasElement, layer: Layer, viewport: Viewport, runtime: RenderRuntime): HTMLCanvasElement {
  const style = layerStyle(layer);
  const shadow = style.dropShadow?.enabled ? style.dropShadow : undefined;
  const stroke = style.stroke?.enabled && style.stroke.width > 0 ? style.stroke : undefined;
  if (!shadow && !stroke) return source;
  const result = runtime.pool.acquire(source.width, source.height);
  const context = requireContext(result);
  if (shadow) {
    const shadowSurface = runtime.pool.acquire(source.width, source.height);
    const shadowContext = requireContext(shadowSurface);
    shadowContext.shadowColor = shadow.color;
    shadowContext.shadowBlur = Math.max(0, shadow.blur) * viewport.dpr;
    shadowContext.shadowOffsetX = shadow.offsetX * viewport.dpr;
    shadowContext.shadowOffsetY = shadow.offsetY * viewport.dpr;
    shadowContext.globalAlpha = Math.max(0, Math.min(1, shadow.opacity));
    shadowContext.drawImage(source, 0, 0);
    shadowContext.globalCompositeOperation = 'destination-out';
    shadowContext.globalAlpha = 1;
    shadowContext.shadowColor = 'transparent';
    shadowContext.drawImage(source, 0, 0);
    context.drawImage(shadowSurface, 0, 0);
    runtime.pool.release(shadowSurface);
  }
  if (stroke) {
    const outline = runtime.pool.acquire(source.width, source.height);
    const outlineContext = requireContext(outline);
    const radius = Math.max(1, Math.ceil(stroke.width * viewport.dpr));
    const samples = Math.max(12, Math.min(64, Math.ceil(radius * Math.PI * 2)));
    for (let sample = 0; sample < samples; sample += 1) {
      const angle = sample / samples * Math.PI * 2;
      outlineContext.drawImage(source, Math.cos(angle) * radius, Math.sin(angle) * radius);
    }
    outlineContext.globalCompositeOperation = 'source-in';
    outlineContext.globalAlpha = Math.max(0, Math.min(1, stroke.opacity));
    outlineContext.fillStyle = stroke.color;
    outlineContext.fillRect(0, 0, outline.width, outline.height);
    outlineContext.globalCompositeOperation = 'destination-out';
    outlineContext.globalAlpha = 1;
    outlineContext.drawImage(source, 0, 0);
    context.drawImage(outline, 0, 0);
    runtime.pool.release(outline);
  }
  context.drawImage(source, 0, 0);
  return result;
}

export function partitionClippingLayers(layers: readonly Layer[]): Layer[][] {
  const chains: Layer[][] = [];
  for (const layer of [...layers].reverse()) {
    const current = chains.at(-1);
    if (layer.clipping && current && current[0]?.kind !== 'adjustment') current.push(layer);
    else chains.push([layer]);
  }
  return chains;
}

function surfaceKey(viewport: Viewport, parentTransform: Matrix): string {
  return `${viewport.width}:${viewport.height}:${viewport.zoom}:${viewport.panX}:${viewport.panY}:${viewport.dpr}:${parentTransform.join(':')}:${extensionRendererGeneration()}`;
}

function renderImage(layer: import('../core/model').ImageLayer, viewport: Viewport, transform: Matrix, runtime: RenderRuntime): { canvas: HTMLCanvasElement; tiles: number } {
  const image = nativeDocumentImage(runtime.document, layer);
  const canvas = runtime.pool.acquire(viewport.width * viewport.dpr, viewport.height * viewport.dpr);
  const context = requireContext(canvas);
  setMatrix(context, viewportMatrix(viewport, transform));
  drawImageInFrame(context, image, image.width, image.height, layer.image.frame);
  return { canvas, tiles: 0 };
}

function renderVisualLayer(layer: Exclude<Layer, AdjustmentLayer>, viewport: Viewport, parentTransform: Matrix, runtime: RenderRuntime): { canvas: HTMLCanvasElement; tiles: number } {
  const key = `${surfaceKey(viewport, parentTransform)}:images:${nativeDocumentImageGeneration(runtime.document)}:text:${nativeDocumentTextGeneration(runtime.document)}`;
  const cached = runtime.documentCache.layers.get(layer.id);
  if (cached?.key === key) {
    runtime.stats.layerHits += 1;
    return { canvas: cached.canvas, tiles: 0 };
  }
  runtime.stats.layerMisses += 1;
  const effective = multiplyMatrix(parentTransform, layer.transform);
  const rendered = layer.kind === 'raster'
    ? renderRaster(layer, viewport, effective, runtime)
    : layer.kind === 'image'
      ? renderImage(layer, viewport, effective, runtime)
      : layer.kind === 'group'
        ? renderGroup(layer, viewport, parentTransform, runtime)
        : renderExtension(layer, viewport, parentTransform, runtime);
  if (layer.mask?.enabled) {
    const context = requireContext(rendered.canvas);
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.globalAlpha = 1;
    context.globalCompositeOperation = 'destination-in';
    const maskSurface = maskCanvas(layer.mask, effective, viewport, runtime);
    context.drawImage(maskSurface, 0, 0);
    runtime.pool.release(maskSurface);
    context.globalCompositeOperation = 'source-over';
  }
  runtime.documentCache.layers.set(layer.id, { key, canvas: rendered.canvas });
  if (cached) {
    runtime.stats.retainedBytes -= cached.canvas.width * cached.canvas.height * 4;
    runtime.pool.release(cached.canvas);
  }
  else runtime.stats.retainedSurfaces += 1;
  runtime.stats.retainedBytes += rendered.canvas.width * rendered.canvas.height * 4;
  return rendered;
}

function renderGroup(group: GroupLayer, viewport: Viewport, parentTransform: Matrix, runtime: RenderRuntime): { canvas: HTMLCanvasElement; tiles: number } {
  const target = runtime.pool.acquire(viewport.width * viewport.dpr, viewport.height * viewport.dpr);
  const context = requireContext(target);
  const childParentTransform = multiplyMatrix(parentTransform, group.transform);
  let tiles = 0;
  for (const chain of partitionClippingLayers(group.children)) {
    const base = chain[0];
    if (!base || !base.visible || base.opacity <= 0) continue;
    if (base.kind === 'adjustment') {
      applyAdjustment(target, base, viewport, childParentTransform, runtime);
      continue;
    }
    const renderedBase = renderVisualLayer(base, viewport, childParentTransform, runtime);
    tiles += renderedBase.tiles;
    const chainCanvas = runtime.pool.acquire(target.width, target.height);
    const chainContext = requireContext(chainCanvas);
    chainContext.globalAlpha = base.fillOpacity;
    chainContext.drawImage(renderedBase.canvas, 0, 0);
    const baseAlpha = runtime.pool.acquire(target.width, target.height);
    const baseAlphaContext = requireContext(baseAlpha);
    baseAlphaContext.globalAlpha = base.fillOpacity;
    baseAlphaContext.drawImage(renderedBase.canvas, 0, 0);

    for (const clipped of chain.slice(1)) {
      if (!clipped.visible || clipped.opacity <= 0) continue;
      if (clipped.kind === 'adjustment') {
        applyAdjustment(chainCanvas, clipped, viewport, childParentTransform, runtime, baseAlpha);
        continue;
      }
      const rendered = renderVisualLayer(clipped, viewport, childParentTransform, runtime);
      tiles += rendered.tiles;
      const clippedSurface = runtime.pool.acquire(rendered.canvas.width, rendered.canvas.height);
      const layerContext = requireContext(clippedSurface);
      layerContext.drawImage(rendered.canvas, 0, 0);
      layerContext.globalCompositeOperation = 'destination-in';
      layerContext.drawImage(baseAlpha, 0, 0);
      chainContext.globalAlpha = clipped.opacity * clipped.fillOpacity;
      chainContext.globalCompositeOperation = canvasBlendModes[clipped.blendMode];
      chainContext.drawImage(clippedSurface, 0, 0);
      runtime.pool.release(clippedSurface);
    }
    const composited = styledSurface(chainCanvas, base, viewport, runtime);
    context.globalAlpha = base.opacity;
    context.globalCompositeOperation = canvasBlendModes[base.blendMode];
    context.drawImage(composited, 0, 0);
    if (composited !== chainCanvas) runtime.pool.release(composited);
    runtime.pool.release(chainCanvas);
    runtime.pool.release(baseAlpha);
  }
  const filters = smartFilters(group);
  if (filters.some((filter) => filter.enabled)) {
    const image = context.getImageData(0, 0, target.width, target.height);
    applySmartFiltersToPixels(image.data, target.width, target.height, filters);
    context.putImageData(image, 0, 0);
  }
  return { canvas: target, tiles };
}

function drawCheckerboard(context: CanvasRenderingContext2D, document: EditorDocument, viewport: Viewport): void {
  context.setTransform(viewport.dpr, 0, 0, viewport.dpr, 0, 0);
  context.fillStyle = '#101c22';
  context.fillRect(0, 0, viewport.width, viewport.height);
  const size = Math.max(4, Math.min(12, 8 * viewport.zoom));
  const left = viewport.panX;
  const top = viewport.panY;
  const width = document.width * viewport.zoom;
  const height = document.height * viewport.zoom;
  context.save();
  context.beginPath();
  context.rect(left, top, width, height);
  context.clip();
  context.fillStyle = '#d9ddd8';
  context.fillRect(left, top, width, height);
  context.fillStyle = '#bec3bd';
  const firstColumn = Math.max(0, Math.floor(-left / size));
  const firstRow = Math.max(0, Math.floor(-top / size));
  const lastColumn = Math.min(Math.ceil(width / size), Math.ceil((viewport.width - left) / size));
  const lastRow = Math.min(Math.ceil(height / size), Math.ceil((viewport.height - top) / size));
  for (let y = firstRow; y < lastRow; y += 1) {
    for (let x = firstColumn; x < lastColumn; x += 1) {
      if ((x + y) % 2 === 0) context.fillRect(left + x * size, top + y * size, size, size);
    }
  }
  context.restore();
}

/** Layer document-space bounds, as the SSV computes them (for preview highlights). */
function documentBoundsOf(document: EditorDocument, id: string): Rect | null {
  const layer = findLayer(document.root, id);
  if (!layer) return null;
  const local = layerBounds(layer);
  const matrix = layerDocumentTransform(document.root, id);
  return local && matrix ? transformRectBoundsPrecise(matrix, local) : null;
}

/** A shallow copy of the tree in which only `keep` subtrees (and their ancestors) stay visible. */
function isolateTree(group: GroupLayer, keep: ReadonlySet<string>): { group: GroupLayer; any: boolean } {
  let any = false;
  const children = group.children.map((child): Layer => {
    if (keep.has(child.id)) { any = true; return child; }
    if (child.kind === 'group') {
      const inner = isolateTree(child, keep);
      if (inner.any) { any = true; return inner.group; }
    }
    return { ...child, visible: false } as Layer;
  });
  return { group: { ...group, children }, any };
}

export class Canvas2DRenderer implements Renderer {
  private readonly documents = new WeakMap<EditorDocument, DocumentRenderCache>();
  private readonly trackedDocuments = new Set<EditorDocument>();
  private readonly tileSurfaces = new WeakMap<RasterTileStore, Map<string, { revision: number; canvas: HTMLCanvasElement }>>();
  private readonly pool = new CanvasPool();
  private readonly totals: RendererCacheStats = { tileHits: 0, tileMisses: 0, layerHits: 0, layerMisses: 0, canvasAllocations: 0, retainedSurfaces: 0, retainedBytes: 0 };
  private readonly pending = new Map<HTMLCanvasElement, { document: EditorDocument; viewport: Viewport; onPresent?: (stats: RenderStats) => void }>();
  private frame = 0;

  private runtime(document: EditorDocument, documentCache?: DocumentRenderCache): RenderRuntime {
    let cache = documentCache ?? this.documents.get(document);
    if (!cache) {
      cache = { layers: new Map() };
      this.documents.set(document, cache);
      this.trackedDocuments.add(document);
    }
    this.totals.canvasAllocations = retainedCanvasAllocations + this.pool.allocations;
    return { document, documentCache: cache, tileSurfaces: this.tileSurfaces, pool: this.pool, stats: this.totals };
  }

  render(document: EditorDocument, target: HTMLCanvasElement, viewport: Viewport): RenderStats {
    const started = performance.now();
    const tileHits = this.totals.tileHits;
    const layerHits = this.totals.layerHits;
    const tileMisses = this.totals.tileMisses;
    const layerMisses = this.totals.layerMisses;
    const pixelWidth = Math.max(1, Math.floor(viewport.width * viewport.dpr));
    const pixelHeight = Math.max(1, Math.floor(viewport.height * viewport.dpr));
    assertCanvasAllocation(pixelWidth, pixelHeight);
    if (target.width !== pixelWidth || target.height !== pixelHeight) {
      target.width = pixelWidth;
      target.height = pixelHeight;
    }
    target.style.width = `${viewport.width}px`;
    target.style.height = `${viewport.height}px`;
    const context = requireContext(target);
    context.clearRect(0, 0, target.width, target.height);
    if (!viewport.transparentBackground) drawCheckerboard(context, document, viewport);
    const rendered = renderGroup(document.root, viewport, IDENTITY_MATRIX, this.runtime(document));
    // Layers larger than the document (full-bleed backgrounds) must not paint
    // outside the canvas: clip the composite to the document bounds so the
    // artboard stays clearly delimited in the workspace.
    context.save();
    context.setTransform(viewport.dpr, 0, 0, viewport.dpr, 0, 0);
    context.beginPath();
    context.rect(viewport.panX, viewport.panY, document.width * viewport.zoom, document.height * viewport.zoom);
    context.clip();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.globalAlpha = 1;
    context.globalCompositeOperation = 'source-over';
    context.drawImage(rendered.canvas, 0, 0);
    context.restore();
    this.pool.release(rendered.canvas);
    this.totals.canvasAllocations = retainedCanvasAllocations + this.pool.allocations;
    const hitCount = this.totals.tileHits - tileHits + this.totals.layerHits - layerHits;
    const lookupCount = hitCount + this.totals.tileMisses - tileMisses + this.totals.layerMisses - layerMisses;
    return { renderedTiles: rendered.tiles, elapsedMs: performance.now() - started, backend: 'canvas2d', tileCacheHits: this.totals.tileHits - tileHits, layerCacheHits: this.totals.layerHits - layerHits, cacheHitRate: lookupCount > 0 ? hitCount / lookupCount : 1, canvasAllocations: this.totals.canvasAllocations };
  }

  schedule(document: EditorDocument, target: HTMLCanvasElement, viewport: Viewport, onPresent?: (stats: RenderStats) => void): void {
    this.pending.set(target, { document, viewport: { ...viewport }, ...(onPresent ? { onPresent } : {}) });
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      const presentations = [...this.pending.entries()];
      this.pending.clear();
      for (const [canvas, item] of presentations) item.onPresent?.(this.render(item.document, canvas, item.viewport));
    });
  }

  invalidate(document: EditorDocument, invalidation: DamageSet): void {
    const cache = this.documents.get(document);
    if (!cache) return;
    const ids = new Set([...invalidation.layerIds, ...invalidation.tiles.map((tile) => tile.layerId)]);
    if (invalidation.fullDocument) ids.add(document.root.id);
    for (const id of [...ids]) {
      let current = id;
      while (current) {
        ids.add(current);
        const location = findLayerParent(document.root, current);
        if (!location) break;
        current = location.parent.id;
      }
    }
    if (invalidation.fullDocument) {
      for (const id of cache.layers.keys()) ids.add(id);
    }
    for (const id of ids) {
      const entry = cache.layers.get(id);
      if (!entry) continue;
      cache.layers.delete(id);
      this.pool.release(entry.canvas);
      this.totals.retainedSurfaces = Math.max(0, this.totals.retainedSurfaces - 1);
      this.totals.retainedBytes = Math.max(0, this.totals.retainedBytes - entry.canvas.width * entry.canvas.height * 4);
    }
  }

  flatten(document: EditorDocument, scale = 1): HTMLCanvasElement {
    if (!Number.isFinite(scale) || scale <= 0) throw new Error('Flatten scale must be a positive finite number.');
    const target = createCanvas(document.width * scale, document.height * scale);
    const viewport: Viewport = {
      width: document.width * scale,
      height: document.height * scale,
      zoom: scale,
      panX: 0,
      panY: 0,
      dpr: 1,
    };
    const localCache: DocumentRenderCache = { layers: new Map() };
    const rendered = renderGroup(document.root, viewport, IDENTITY_MATRIX, this.runtime(document, localCache));
    const context = requireContext(target);
    context.clearRect(0, 0, target.width, target.height);
    context.drawImage(rendered.canvas, 0, 0);
    this.pool.release(rendered.canvas);
    for (const entry of localCache.layers.values()) this.pool.release(entry.canvas);
    this.totals.retainedSurfaces = Math.max(0, this.totals.retainedSurfaces - localCache.layers.size);
    this.totals.retainedBytes = Math.max(0, this.totals.retainedBytes - [...localCache.layers.values()].reduce((sum, entry) => sum + entry.canvas.width * entry.canvas.height * 4, 0));
    return target;
  }

  renderPreview(document: EditorDocument, options: PreviewRenderOptions): PreviewRender {
    const { scale } = options;
    if (!Number.isFinite(scale) || scale <= 0) throw new Error('Preview scale must be a positive finite number.');
    const requested = options.region ?? { x: 0, y: 0, width: document.width, height: document.height };
    const left = Math.max(0, requested.x);
    const top = Math.max(0, requested.y);
    const right = Math.min(document.width, requested.x + requested.width);
    const bottom = Math.min(document.height, requested.y + requested.height);
    if (!(right > left && bottom > top)) throw new Error('The preview region does not overlap the document.');
    const region = { x: left, y: top, width: right - left, height: bottom - top };
    const width = Math.max(1, Math.round(region.width * scale));
    const height = Math.max(1, Math.round(region.height * scale));
    assertCanvasAllocation(width, height);
    const source = options.isolate?.length
      ? { ...document, root: isolateTree(document.root, new Set(options.isolate)).group }
      : document;
    const viewport: Viewport = { width, height, zoom: scale, panX: -region.x * scale, panY: -region.y * scale, dpr: 1 };
    const target = createCanvas(width, height);
    const localCache: DocumentRenderCache = { layers: new Map() };
    const rendered = renderGroup(source.root, viewport, IDENTITY_MATRIX, this.runtime(source, localCache));
    const context = requireContext(target);
    context.clearRect(0, 0, width, height);
    if (options.background) {
      context.fillStyle = options.background;
      context.fillRect(0, 0, width, height);
    }
    context.drawImage(rendered.canvas, 0, 0);
    this.pool.release(rendered.canvas);
    for (const entry of localCache.layers.values()) this.pool.release(entry.canvas);
    this.totals.retainedSurfaces = Math.max(0, this.totals.retainedSurfaces - localCache.layers.size);
    this.totals.retainedBytes = Math.max(0, this.totals.retainedBytes - [...localCache.layers.values()].reduce((sum, entry) => sum + entry.canvas.width * entry.canvas.height * 4, 0));
    if (source !== document) { this.documents.delete(source); this.trackedDocuments.delete(source); }
    const bounds: Record<string, Rect | null> = {};
    for (const id of options.highlight ?? []) {
      const box = documentBoundsOf(document, id);
      bounds[id] = box;
      if (!box) continue;
      context.save();
      context.lineWidth = 2;
      context.setLineDash([6, 4]);
      context.strokeStyle = '#ff2d95';
      context.strokeRect((box.x - region.x) * scale + 1, (box.y - region.y) * scale + 1, Math.max(0, box.width * scale - 2), Math.max(0, box.height * scale - 2));
      context.restore();
    }
    return { canvas: target, region, bounds };
  }

  flattenTiles(document: EditorDocument, scale = 1, tileSize = 4_096): FlattenedTile[] {
    if (!Number.isFinite(scale) || scale <= 0 || !Number.isInteger(tileSize) || tileSize < 256) throw new Error('Tiled flatten parameters are invalid.');
    const width = Math.ceil(document.width * scale);
    const height = Math.ceil(document.height * scale);
    const tiles: FlattenedTile[] = [];
    for (let y = 0; y < height; y += tileSize) for (let x = 0; x < width; x += tileSize) {
      const tileWidth = Math.min(tileSize, width - x);
      const tileHeight = Math.min(tileSize, height - y);
      const canvas = createCanvas(tileWidth, tileHeight);
      const viewport: Viewport = { width: tileWidth, height: tileHeight, zoom: scale, panX: -x, panY: -y, dpr: 1 };
      const localCache: DocumentRenderCache = { layers: new Map() };
      const rendered = renderGroup(document.root, viewport, IDENTITY_MATRIX, this.runtime(document, localCache));
      requireContext(canvas).drawImage(rendered.canvas, 0, 0);
      this.pool.release(rendered.canvas);
      for (const entry of localCache.layers.values()) this.pool.release(entry.canvas);
      this.totals.retainedSurfaces = Math.max(0, this.totals.retainedSurfaces - localCache.layers.size);
      this.totals.retainedBytes = Math.max(0, this.totals.retainedBytes - [...localCache.layers.values()].reduce((sum, entry) => sum + entry.canvas.width * entry.canvas.height * 4, 0));
      tiles.push({ canvas, x, y, scale });
    }
    return tiles;
  }

  disposeDocument(document: EditorDocument): void {
    const cache = this.documents.get(document);
    if (cache) for (const entry of cache.layers.values()) {
      this.pool.release(entry.canvas);
      this.totals.retainedSurfaces = Math.max(0, this.totals.retainedSurfaces - 1);
      this.totals.retainedBytes = Math.max(0, this.totals.retainedBytes - entry.canvas.width * entry.canvas.height * 4);
    }
    this.documents.delete(document);
    this.trackedDocuments.delete(document);
    for (const layer of walkLayers(document.root)) {
      const stores = [...(layer.kind === 'raster' ? [layer.raster] : []), ...(layer.mask ? [layer.mask.tiles] : [])];
      for (const store of stores) {
        const surfaces = this.tileSurfaces.get(store);
        if (!surfaces) continue;
        for (const item of surfaces.values()) {
          this.pool.release(item.canvas);
          this.totals.retainedSurfaces = Math.max(0, this.totals.retainedSurfaces - 1);
          this.totals.retainedBytes = Math.max(0, this.totals.retainedBytes - item.canvas.width * item.canvas.height * 4);
        }
        this.tileSurfaces.delete(store);
      }
    }
    for (const [target, item] of this.pending) if (item.document === document) this.pending.delete(target);
  }

  cacheStats(): RendererCacheStats {
    return { ...this.totals, canvasAllocations: retainedCanvasAllocations + this.pool.allocations };
  }

  dispose(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.pending.clear();
    for (const document of this.trackedDocuments) this.disposeDocument(document);
    this.pool.clear();
  }
}

export function flattenLayerNames(layer: Layer): string[] {
  return layer.kind === 'group'
    ? [layer.name, ...layer.children.flatMap(flattenLayerNames)]
    : [layer.name];
}
